// Native bridge contract for StoreKit 2 in-app purchases (DUE-161
// subproject C, task 5). `ios/App/App/StoreKitPlugin.swift` (task 7)
// implements exactly this surface on the Swift side; this module is the
// source of truth for it on the JS side, written ahead of the native
// plugin so `./ios.ts` and its tests can build against a stable contract.
//
// `registerPlugin` is safe to call unconditionally at import time (same
// reasoning as the Capacitor plugin imports in `ios.ts`): it builds a lazy
// proxy immediately and only dispatches to the native bridge / web
// fallback per *call*. This module is pulled into every bundle path via
// `./ios.ts` <- `./index.ts` (including the Telegram bundle), so it must
// stay side-effect-free at import time — and it is.
import { registerPlugin } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

export interface StoreKitProduct {
  id: string;
  displayPrice: string;
  displayName: string;
  type: string;
}

export interface StoreKitGetProductsResult {
  products: StoreKitProduct[];
}

export interface StoreKitPurchaseResult {
  /** StoreKit 2 `VerificationResult.jwsRepresentation` on a successful
   * purchase. Absent when `cancelled` is true. */
  jws?: string;
  cancelled?: boolean;
}

export interface StoreKitTransaction {
  jws: string;
}

export interface StoreKitCurrentEntitlementsResult {
  transactions: StoreKitTransaction[];
}

/** Payload of the native `transactionUpdated` event (`StoreKitPlugin.swift`'s
 * `Transaction.updates` listener) — renewals, upgrades/downgrades, Ask-to-Buy
 * approvals, and transactions arriving from other devices on the same
 * Apple ID. */
export interface StoreKitTransactionUpdatedEvent {
  jws: string;
}

export interface StoreKitPluginApi {
  getProducts(options: { ids: string[] }): Promise<StoreKitGetProductsResult>;
  purchase(options: { productId: string }): Promise<StoreKitPurchaseResult>;
  currentEntitlements(): Promise<StoreKitCurrentEntitlementsResult>;
  finishTransaction(options: { transactionId: string }): Promise<void>;
  addListener(
    eventName: "transactionUpdated",
    listenerFunc: (event: StoreKitTransactionUpdatedEvent) => void,
  ): Promise<PluginListenerHandle>;
}

export const StoreKit = registerPlugin<StoreKitPluginApi>("StoreKit");

/**
 * Extracts `transactionId` from a StoreKit 2 JWS *without* verifying its
 * signature. This is not a trust boundary — trust is established
 * server-side by `POST /api/payments/appstore/verify` before this value is
 * ever used. It exists purely so the iOS adapter can tell the native
 * plugin which pending transaction to `finishTransaction()` once the
 * backend confirms fulfillment: `purchase()` only hands back the signed
 * JWS, not a bare transaction id.
 *
 * Returns `null` on any malformed input (missing segment, bad
 * base64/JSON) instead of throwing — a decode failure here must never
 * block returning a "paid" outcome for a purchase the server already
 * verified and fulfilled. It just means this app launch skips finishing
 * that specific transaction; StoreKit re-delivers unfinished transactions
 * (via `currentEntitlements()` / `Transaction.updates`), so it gets
 * finished on a later pass.
 */
export function decodeJwsTransactionId(jws: string): string | null {
  const segment = jws.split(".")[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims: unknown = JSON.parse(atob(padded));
    if (
      typeof claims === "object" &&
      claims !== null &&
      typeof (claims as { transactionId?: unknown }).transactionId === "string"
    ) {
      return (claims as { transactionId: string }).transactionId;
    }
    return null;
  } catch {
    return null;
  }
}
